import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { base_url } from "Backend/api";
import { addressListFromApi } from "utils/addressListFromApi";
import { Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import Icon from "../../components/AppIcon";
import AppSpinner from "components/ui/AppSpinner";
import Lottie from "lottie-react";
import locationDeleteAnimation from "../../../public/assets/lottie/LocationDelete.json";

function AddressDeleteConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-3 font-[cairo]">
      <div className="relative w-full max-w-[360px] rounded-[8px] bg-white p-6 pt-4 shadow-[0_12px_30px_rgba(0,0,0,0.18)] md:p-4 md:pt-9">
        <button
          type="button"
          onClick={onCancel}
          className="absolute right-3.5 top-3.5 text-[#1F2937] transition hover:opacity-75"
          aria-label="إغلاق"
        >
          <Icon name="X" size={20} />
        </button>

        <div className="mx-auto mb-2.5 flex h-[106px] w-[106px] items-center justify-center rounded-full ">
          <Lottie
            animationData={locationDeleteAnimation}
            loop
            className="h-[120px] w-[120px]"
          />
        </div>

        <h3 className="font-[cairo] text-center text-[20px] font-bold leading-tight text-[#1F2937] md:text-[22px]">
          {title}
        </h3>
        <p className="mt-1 font-[cairo] mb-2 text-center text-[13px] font-medium leading-tight text-[#8B8B8B] md:text-[14px]">
          {description}
        </p>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="h-[40px] rounded-[8px] bg-[#F93209] text-[14px] font-medium text-white transition hover:bg-[#e82f05]"
          >
            {confirmLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="h-[40px] rounded-[8px] border border-[#F93209] text-[14px] font-medium text-[#F93209] transition hover:bg-[#FFF3EF]"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

function formatAddressLocationParts(addr) {
  const parts = [addr?.province, addr?.city, addr?.district, addr?.street]
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);
  return parts.join(" - ");
}

export default function MyAddresses() {
    const navigate = useNavigate();
    const [addresses, setAddresses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [showDialog, setShowDialog] = useState(false);
const [selectedAddr, setSelectedAddr] = useState(null);
const [showDeleteModal, setShowDeleteModal] = useState(false);
const [selectedSlug, setSelectedSlug] = useState(null);
const openDeleteModal = (slug) => {
  setSelectedSlug(slug);
  setShowDeleteModal(true);
};

const closeDeleteModal = () => {
  setShowDeleteModal(false);
  setSelectedSlug(null);
};

    const fetchAddresses = async () => {
        const token = localStorage.getItem("token");

        try {
            const res = await fetch(`${base_url}/api/addresses`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await res.json();
            if (data.status) {
                setAddresses(addressListFromApi(data.data));
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAddresses();
    }, []);

    const setActiveAddress = async (slug, index) => {
      try {
          const token = localStorage.getItem("token");
  
          const res = await fetch(`${base_url}/api/addresses/set-active`, {
              method: "POST",
              headers: {
                  Authorization: `Bearer ${token}`,
                  Accept: "application/json",
                  "Content-Type": "application/json",
              },
              body: JSON.stringify({
                  slug: slug
              }),
          });
  
          if (res.ok) {
              await fetchAddresses();
              window.dispatchEvent(new CustomEvent("addressesUpdated"));
              toast.success("تم إضافة العنوان النشط بنجاح");
              setSelectedIndex(index);
          }
      } catch (err) {
          console.error(err);
      }
  };


    const deleteAddress = async (slug) => {
        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${base_url}/api/addresses/${slug}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (res.ok) {
                setAddresses((prev) => prev.filter((addr) => addr.slug !== slug));
                window.dispatchEvent(new CustomEvent("addressesUpdated"));
                toast.success("تم حذف العنوان بنجاح");
                return true;
            }
            return false;
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    const confirmDeleteAddress = async () => {
        if (!selectedSlug) return;
        const ok = await deleteAddress(selectedSlug);
        if (ok) closeDeleteModal();
    };

    const checkCart = async () => {
  const token = localStorage.getItem("token");
  const userSlug = localStorage.getItem("slug");

  const res = await fetch(`${base_url}/api/cart/has`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      user_slug: userSlug,
    }),
  });

  const data = await res.json();
  return data?.data?.has_cart ?? false;
};

const clearCart = async () => {
  const token = localStorage.getItem("token");

  await fetch(`${base_url}/api/cart`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

const handleAddressClick = async (addr, index) => {
  try {
    const hasCart = await checkCart();

    if (hasCart) {
      setSelectedAddr({ addr, index });
      setShowDialog(true);
    } else {
      await setActiveAddress(addr.slug, index);
    }
  } catch (err) {
    console.error(err);
  }
};

const confirmChangeAddress = async () => {
  if (!selectedAddr) return;

  await clearCart(); 

  await setActiveAddress(selectedAddr.addr.slug, selectedAddr.index);

  setShowDialog(false);
  toast.success("تم حذف محتوى السلة بنجاح");
};

    return (
        <div className="bg-gray-50 min-h-screen w-full max-w-3xl mx-auto  font-[cairo] font-[cairo]" dir="rtl">

            {/* HEADER */}
            <div className="flex items-center justify-between px-4 h-12 bg-white">

                <img
                    onClick={() => navigate('/')}
                    src="assets/images/bbackk.svg"
                    className="w-3 h-3 flex items-center justify-center cursor-pointer" />

                <h2 className="font-bold font-[cairo] text-[16px]">عناويني</h2>


                <img
                    onClick={() => navigate("/add-address")}
                    src="assets/images/batta.svg"
                    className="w-12 h-12 mt-3  cursor-pointer shadow-none" />


            </div>

            {/* ALERTS */}
            <div className="px-4 mt-3 space-y-2">
                {[
                    "سيتم التوصيل إلى العنوان الذي سيتم اختياره",
                    "تعرض المطاعم في الصفحة الرئيسية ضمن نطاق 2 كم من العنوان المحدد",
                ].map((text, i) => (
                    <div
                        key={i}
                        className="bg-[#FFF4E5] border border-[#FFD8A8] rounded-[4px] p-3"
                    >
                        <p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-[#F93209]">
                            <Icon name="AlertCircle" size={14} className="shrink-0" strokeWidth={2.25} color="#F93209" />
                            تنبيه
                        </p>
                        <p className="text-xs text-gray-700">{text}</p>
                    </div>
                ))}
            </div>

            {/* LIST */}
            <div className="p-4 space-y-3">
                {isLoading ? (
                    <div className="flex min-h-[40vh] items-center justify-center">
                        <AppSpinner />
                    </div>
                ) : (Array.isArray(addresses) ? addresses : []).map((addr, index) => {
                    const locationDetails = formatAddressLocationParts(addr);
                    return (
                    <div
                        key={addr.id}
                        onClick={() => handleAddressClick(addr, index)}
                        className={`relative rounded-[4px] border p-4 transition ${addr.is_active
                                ? "border-[#F93209] border-2 bg-white cursor-pointer"
                                : "border-gray-200 bg-white cursor-pointer"
                            }`}
                    >
                        {/* ACTIONS */}
                        <div className="absolute left-3 top-3 flex gap-3 text-gray-500">
                            <Trash2
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteModal(addr.slug);
                                }} size={18} className="cursor-pointer text-[#F93209]" />

                        </div>

                        {addr.is_active && (
                            <span className="absolute left-9 top-3 inline-flex items-center justify-center rounded-full bg-[#F93209] px-2.5 py-[5px] font-[cairo] text-[10px] font-medium leading-none text-white md:text-[11px]">
                                العنوان الافتراضى
                            </span>
                        )}

                        {/* HEADER */}
                        <div className="flex ">


                            {/* CUSTOM RADIO */}
                            <div
                                className={`w-5 h-5 rounded-full border flex items-center justify-center ${addr.is_active
                                        ? "border-red-500"
                                        : "border-gray-300"
                                    }`}
                            >
                                {addr.is_active && (
                                    <div className="w-3 h-3 bg-[#F93209] rounded-full" />
                                )}
                            </div>
                            <h3 className="font-[cairo] font-bold text-[13px] px-2">
                                {addr.receiver_name} <span className="text-[#00B200] text-[8px]">({addr.title})</span>
                            </h3>
                        </div>

                        {/* ADDRESS */}
                        <p className="text-[11px] text-gray-600 mt-2 mr-6 leading-6">
                            {addr.description}
                        </p>

                        {locationDetails && (
                          <p className="text-[11px] text-gray-600 mt-1 mr-6 leading-6">
                            {locationDetails}
                          </p>
                        )}

                        {/* PHONE */}
                        <p className="text-[11px] text-gray-500 mt-1 mr-4 flex items-center gap-1">
                            <img src="assets/images/phono.svg" className="w-3 h-3 " /> {addr.receiver_phone}
                        </p>
                    </div>
                );
                })}
            </div>
            <AddressDeleteConfirmDialog
                open={showDeleteModal}
                title="حذف العنوان"
                description="سيتم حذف هذا العنوان ولا يمكن التراجع عن ذلك."
                confirmLabel="حذف العنوان"
                onCancel={closeDeleteModal}
                onConfirm={confirmDeleteAddress}
            />

            {showDialog && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white w-[90%] max-w-sm rounded-lg p-5 text-center">

      <img
        src="assets/images/carrt.svg"
        className="mx-auto mb-3 w-20"
      />

      <h3 className="font-bold text-sm mb-1">تنبيه!</h3>

      <p className="text-xs text-gray-500 mb-4">
        سيتم تفريغ السلة عند تغيير العنوان لاختلاف المطاعم المتاحة.
      </p>

      <div className="flex gap-3">
        {/* confirm */}
        <button
          onClick={confirmChangeAddress}
          className="flex-1 bg-[#F93209] text-white py-2 rounded text-xs"
        >
          تغيير العنوان
        </button>

        {/* cancel */}
        <button
          onClick={() => setShowDialog(false)}
          className="flex-1 border border-red-500 text-red-500 py-2 rounded text-xs"
        >
          إلغاء
        </button>
      </div>
    </div>
  </div>
)}
        </div>
    );
}